import React, { useMemo, useState } from 'react';
import { X, CheckCircle2, AlertTriangle, Loader2, MousePointerClick, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BinLocation, RackStructure, compareSegments } from './types';
import { BinContentSummary } from '../../../common/rack-grid';

/**
 * HealthCheckPanel — konfigurasjons-helsesjekk for lastede reoler (Fase 3).
 *
 * Regler over lastet struktur (motivert av saldoimport-skaden TW 36373423/25 —
 * avvikene hadde vært synlige på ett blikk):
 *  1. Lokasjoner uten sone
 *  2. BinType-avvikere (avviker fra reolens dominerende type)
 *  3. Ikke-plukkbare lokasjoner med saldo (krever saldo-laget lastet)
 *  4. Hull i nummereringen (celler som mangler i matrisen)
 *
 * «Velg alle» per regel setter seleksjonen slik at avvikene kan masseendres direkte.
 */

interface HealthCheckPanelProps {
    structures: Map<string, RackStructure>;
    allLocations: BinLocation[];
    /** Saldo per bin (null = ikke lastet — regel 3 viser da lasteindikator/hint) */
    contents: Map<string, BinContentSummary> | null;
    isLoadingContents: boolean;
    onSelectBins: (binCodes: string[]) => void;
    onClose: () => void;
}

interface RuleResult {
    key: string;
    label: string;
    binCodes: string[];
    /** Ekstra detaljlinjer (f.eks. koordinater for hull) */
    details?: string[];
    selectable: boolean;
    pending?: boolean;
}

export const HealthCheckPanel: React.FC<HealthCheckPanelProps> = ({
    structures,
    allLocations,
    contents,
    isLoadingContents,
    onSelectBins,
    onClose
}) => {
    const { t } = useTranslation();
    const [expandedRule, setExpandedRule] = useState<string | null>(null);

    const rules = useMemo((): RuleResult[] => {
        // 1. Uten sone
        const missingZone = allLocations
            .filter(l => !l.stcZoneID || l.stcZoneID.trim() === '')
            .map(l => l.binCode);

        // 2. BinType-avvikere: dominerende type per reol, avvikere flagges
        const outliers: string[] = [];
        const outlierDetails: string[] = [];
        structures.forEach((structure, rackNumber) => {
            if (structure.locations.length < 2) return;
            const counts = new Map<number, number>();
            for (const loc of structure.locations) {
                counts.set(loc.binTypeNo, (counts.get(loc.binTypeNo) ?? 0) + 1);
            }
            let dominant = structure.locations[0].binTypeNo;
            let max = 0;
            counts.forEach((count, type) => {
                if (count > max) {
                    max = count;
                    dominant = type;
                }
            });
            // Alt avviker = ingen dominans å avvike fra (unngå å flagge blandingsreoler i sin helhet)
            if (max === structure.locations.length) return;
            const rackOutliers = structure.locations.filter(l => l.binTypeNo !== dominant);
            if (rackOutliers.length > 0 && rackOutliers.length <= structure.locations.length / 2) {
                outliers.push(...rackOutliers.map(l => l.binCode));
                outlierDetails.push(
                    t('manager.modules.rackBuilder.health.outlierDetail',
                        { '0': rackNumber, '1': dominant, '2': rackOutliers.length },
                        'Reol {0}: dominerende type {1}, {2} avviker')
                );
            }
        });

        // 3. Ikke-plukkbare med saldo (krever saldo-data)
        let nonPickableWithStock: string[] = [];
        const contentsPending = contents === null;
        if (contents) {
            nonPickableWithStock = allLocations
                .filter(l => l.pickableYesNo === false && (contents.get(l.binCode)?.totalQuantity ?? 0) > 0)
                .map(l => l.binCode);
        }

        // 4. Hull i nummereringen: celler i matrisen uten lokasjon
        const gapDetails: string[] = [];
        structures.forEach((structure, rackNumber) => {
            const present = new Set(structure.locations.map(l => `${l.vertical}-${l.shelf}`));
            const verticals = [...structure.verticals].sort(compareSegments);
            const shelves = [...structure.shelves].sort(compareSegments);
            for (const v of verticals) {
                for (const s of shelves) {
                    if (!present.has(`${v}-${s}`)) {
                        gapDetails.push(`${t('manager.modules.rackBuilder.app.rackLabel', { '0': rackNumber }, 'Reol {0}')}: V${v}/H${s}`);
                    }
                }
            }
        });

        return [
            {
                key: 'missingZone',
                label: t('manager.modules.rackBuilder.health.missingZone', {}, 'Lokasjoner uten sone'),
                binCodes: missingZone,
                selectable: true
            },
            {
                key: 'binTypeOutliers',
                label: t('manager.modules.rackBuilder.health.binTypeOutliers', {}, 'BinType-avvikere (avviker fra reolens dominerende type)'),
                binCodes: outliers,
                details: outlierDetails,
                selectable: true
            },
            {
                key: 'nonPickableWithStock',
                label: t('manager.modules.rackBuilder.health.nonPickableWithStock', {}, 'Ikke-plukkbare lokasjoner med saldo'),
                binCodes: nonPickableWithStock,
                selectable: true,
                pending: contentsPending
            },
            {
                key: 'numberingGaps',
                label: t('manager.modules.rackBuilder.health.numberingGaps', {}, 'Hull i nummereringen (manglende celler)'),
                binCodes: [],
                details: gapDetails,
                selectable: false
            }
        ];
    }, [structures, allLocations, contents, t]);

    const gapCount = rules.find(r => r.key === 'numberingGaps')?.details?.length ?? 0;

    return (
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 flex-shrink-0">
            <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold text-gray-800 uppercase">
                    {t('manager.modules.rackBuilder.health.title', {}, 'Konfigurasjons-helsesjekk')}
                </h4>
                <button
                    onClick={onClose}
                    className="p-0.5 text-gray-500 hover:text-gray-800 rounded"
                    title={t('common.buttons.close', {}, 'Lukk')}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-1">
                {rules.map(rule => {
                    const count = rule.key === 'numberingGaps' ? gapCount : rule.binCodes.length;
                    const isOk = count === 0 && !rule.pending;
                    const isExpanded = expandedRule === rule.key;
                    const hasDetails = (rule.details?.length ?? 0) > 0;

                    return (
                        <div key={rule.key} className="text-xs">
                            <div className="flex items-center gap-2">
                                {rule.pending && isLoadingContents ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 flex-shrink-0" />
                                ) : isOk ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                ) : (
                                    <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" />
                                )}
                                <span className="text-gray-800">{rule.label}</span>
                                {rule.pending ? (
                                    <span className="text-gray-500 italic">
                                        {t('manager.modules.rackBuilder.health.loadingBalances', {}, 'laster saldo...')}
                                    </span>
                                ) : (
                                    <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
                                        isOk ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {count}
                                    </span>
                                )}
                                {rule.selectable && rule.binCodes.length > 0 && (
                                    <button
                                        onClick={() => onSelectBins(rule.binCodes)}
                                        className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                    >
                                        <MousePointerClick className="w-3 h-3" />
                                        {t('manager.modules.rackBuilder.health.selectAll', {}, 'Velg alle')}
                                    </button>
                                )}
                                {hasDetails && (
                                    <button
                                        onClick={() => setExpandedRule(isExpanded ? null : rule.key)}
                                        className="flex items-center text-gray-600 hover:text-gray-800"
                                    >
                                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                        {t('manager.modules.rackBuilder.health.details', {}, 'detaljer')}
                                    </button>
                                )}
                            </div>
                            {isExpanded && hasDetails && (
                                <div className="ml-6 mt-1 text-gray-600 max-h-24 overflow-y-auto space-y-0.5">
                                    {rule.details!.slice(0, 50).map((d, idx) => (
                                        <div key={idx} className="font-mono">{d}</div>
                                    ))}
                                    {rule.details!.length > 50 && (
                                        <div className="italic">
                                            {t('manager.modules.rackBuilder.bulk.andMore', { '0': rule.details!.length - 50 }, '... og {0} flere')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
